import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, CornerDownLeft, Clock, X } from 'lucide-react';
import { Modal, Input, cn } from '../../ui';
import { TONE, categoryMeta, type ReportDefinition } from './reportKit';
import { matchesQuery, searchIndex, readRecent } from './reportPresentation';

/**
 * The report catalogue, as one dialog.
 *
 * It replaces the 300px rail that used to stand permanently beside every open report: a list of 29
 * items is worth a moment of the screen, not a third of it forever. Opened from the workspace header
 * (and from the landing page), it is a search box with the whole catalogue underneath — type two
 * words, press Enter, and the report is open.
 *
 * Everything here is read from the catalogue the API already returns, so a report only ever appears
 * for someone the server would let run it.
 */
export function ReportNavigator({ open, onClose, reports, current, onPick, loading }: {
  open: boolean;
  onClose: () => void;
  reports: ReportDefinition[];
  /** The report already on screen, highlighted so the list says where you are. */
  current?: string;
  onPick: (code: string) => void;
  loading?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [area, setArea] = useState<string | undefined>();
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // A dialog that reopens holding the last search is a dialog that lies about what it is showing.
  useEffect(() => { if (open) { setQuery(''); setArea(undefined); setActive(0); } }, [open]);

  /** Pre-lowered haystacks — the catalogue is searched on every keystroke. */
  const index = useMemo(() => new Map(reports.map((r) => [r.code, searchIndex(r)])), [reports]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of reports) counts.set(r.category, (counts.get(r.category) ?? 0) + 1);
    return [...counts.entries()];
  }, [reports]);

  const matched = useMemo(
    () => reports.filter((r) => matchesQuery(r, query, index.get(r.code))),
    [reports, query, index],
  );
  /** The category rail counts what the SEARCH left, so a count can never promise rows it cannot show. */
  const matchedByCat = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of matched) m.set(r.category, (m.get(r.category) ?? 0) + 1);
    return m;
  }, [matched]);

  const visible = useMemo(
    () => (area ? matched.filter((r) => r.category === area) : matched),
    [matched, area],
  );

  const recent = useMemo(() => {
    if (!open || query.trim() || area) return [];
    const known = new Map(reports.map((r) => [r.code, r]));
    return readRecent().map((c) => known.get(c)).filter((r): r is ReportDefinition => !!r && r.code !== current).slice(0, 4);
  }, [open, query, area, reports, current]);

  useEffect(() => { setActive(0); }, [query, area]);

  // Keep the keyboard cursor in view — arrowing past the fold is otherwise invisible.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (code: string) => { onPick(code); onClose(); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, visible.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const r = visible[active];
      if (r) pick(r.code);
    }
  };

  const Row = ({ r, i }: { r: ReportDefinition; i: number }) => {
    const m = categoryMeta(r.category);
    const isCurrent = r.code === current;
    return (
      <button
        type="button"
        role="option"
        aria-selected={i === active}
        data-active={i === active ? 'true' : undefined}
        onClick={() => pick(r.code)}
        onMouseMove={() => setActive(i)}
        className={cn(
          'flex w-full items-start gap-3 border-l-2 px-3 py-2 text-left transition-colors',
          i === active ? 'border-l-brand-500 bg-brand-500/[0.07]' : 'border-l-transparent hover:bg-surface-2',
        )}
      >
        <span className={cn('mt-0.5 size-1.5 shrink-0 rounded-pill', isCurrent ? TONE[m.tone].rail : 'bg-transparent')} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={cn('truncate text-sm', isCurrent ? 'font-semibold text-content' : 'font-medium text-content')}>{r.name}</span>
            {isCurrent && <span className="shrink-0 type-caption text-subtle">open</span>}
          </span>
          <span className="mt-0.5 line-clamp-1 type-caption text-muted">{r.description}</span>
        </span>
        <span className={cn('mt-0.5 shrink-0 rounded-pill px-2 py-0.5 type-caption', TONE[m.tone].chip)}>{r.category}</span>
      </button>
    );
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Report catalogue" width={880}>
      <div className="flex h-full min-h-0 flex-col gap-3" onKeyDown={onKeyDown}>
        <Input
          ref={searchRef}
          allowClear
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${reports.length} reports by name, category or purpose…`}
          aria-label="Search reports"
          aria-controls="report-navigator-list"
          prefix={<Search size={15} className="text-subtle" />}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-3 sm:flex-row">
          {/* Categories. A horizontal strip on a phone, a rail on a desktop — the same list either way. */}
          <nav
            aria-label="Report categories"
            className="flex shrink-0 gap-1 overflow-x-auto pb-1 sm:w-[186px] sm:flex-col sm:overflow-y-auto sm:pb-0 sm:pr-1"
          >
            <CategoryButton label="All reports" count={matched.length} on={!area} onClick={() => setArea(undefined)} />
            {categories.map(([cat, total]) => (
              <CategoryButton
                key={cat}
                label={cat}
                count={matchedByCat.get(cat) ?? 0}
                total={total}
                on={area === cat}
                tone={categoryMeta(cat).tone}
                onClick={() => setArea(area === cat ? undefined : cat)}
              />
            ))}
          </nav>

          <div
            ref={listRef}
            id="report-navigator-list"
            role="listbox"
            aria-label="Reports"
            className="min-h-0 flex-1 overflow-y-auto rounded-control border border-line bg-surface-2/40 sm:max-h-[56vh]"
          >
            {loading ? (
              <p className="px-3 py-10 text-center text-sm text-muted">Loading the catalogue…</p>
            ) : !visible.length ? (
              <div className="px-3 py-10 text-center">
                <p className="text-sm text-content">Nothing matches “{query}”.</p>
                <p className="mt-1 type-caption text-muted">Try a word from the question you are answering — “owes”, “expiry”, “visits”.</p>
              </div>
            ) : (
              <>
                {recent.length > 0 && (
                  <>
                    <p className="flex items-center gap-1.5 border-b border-line bg-surface px-3 py-1.5 type-overline text-subtle">
                      <Clock className="size-3" /> Recently used
                    </p>
                    {recent.map((r) => (
                      <button
                        key={`recent-${r.code}`}
                        type="button"
                        onClick={() => pick(r.code)}
                        className="flex w-full items-center gap-2 border-l-2 border-l-transparent px-3 py-1.5 text-left text-sm text-content transition-colors hover:bg-surface-2"
                      >
                        <span className="min-w-0 flex-1 truncate">{r.name}</span>
                        <span className="shrink-0 type-caption text-subtle">{r.category}</span>
                      </button>
                    ))}
                    <p className="border-y border-line bg-surface px-3 py-1.5 type-overline text-subtle">
                      {area ?? 'All reports'}
                    </p>
                  </>
                )}
                <div className="divide-y divide-line/70">
                  {visible.map((r, i) => <Row key={r.code} r={r} i={i} />)}
                </div>
              </>
            )}
          </div>
        </div>

        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 type-caption text-subtle">
          <span className="inline-flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> move</span>
          <span className="inline-flex items-center gap-1"><Kbd><CornerDownLeft className="size-3" /></Kbd> open</span>
          <span className="inline-flex items-center gap-1"><Kbd>Esc</Kbd> close</span>
          <span className="ml-auto">{visible.length} of {reports.length} reports</span>
        </p>
      </div>
    </Modal>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-grid h-4 min-w-4 place-items-center rounded border border-line bg-surface-2 px-1 font-sans text-[10px] leading-none text-muted">
      {children}
    </kbd>
  );
}

function CategoryButton({ label, count, total, on, tone, onClick }: {
  label: string; count: number; total?: number; on: boolean;
  tone?: keyof typeof TONE; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'flex shrink-0 items-center gap-2 rounded-control px-2.5 py-1.5 text-left text-sm transition-colors sm:w-full',
        on ? 'bg-brand-500/[0.10] font-medium text-content' : 'text-muted hover:bg-surface-2 hover:text-content',
        count === 0 && !on && 'opacity-45',
      )}
    >
      {tone && <span className={cn('size-1.5 shrink-0 rounded-pill', TONE[tone].rail)} aria-hidden />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="shrink-0 tabular-nums type-caption text-subtle">
        {count}{total != null && count !== total ? `/${total}` : ''}
      </span>
      {on && <X className="size-3 shrink-0 text-subtle sm:hidden" aria-hidden />}
    </button>
  );
}
